import { HttpClient} from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { finalize } from 'rxjs';
import { EndpointsService } from 'src/app/services/endpoints.service';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { UiBlockService } from 'src/app/services/ui-block.service';
import { UserApprovalComponent, UserApprovalForm } from '../user-approval/user-approval.component';
import { reference } from '@popperjs/core';
import { ChangeDetectorRef } from '@angular/core';


export interface UserManagementResponse {
  users: UserManagementRow[];
  expiringSoonDays: number;
  inactivityDays: number;
}
export interface UserManagementRow {
  id: number;
  userName: string;
  email: string;
  organizationName: string;
  organizationType: string;
  userState: string;
  dueDate: string;
  approvalDate: string;
  adminManager: string;
  referenceLicense: string;
  agreementType?: string;
  paymentStatus?: string;
  userLastLogin: string | null;
  role?: string;
  country?: string;
  storage?: string; // e.g. "1 / 10 GB"
  agreementDuration?: string; // 'LIMITED' | 'UNLIMITED'
  userApprovalRequestStr?: string; // JSON string with displayName, etc.
  groups?: string[];
  systemRoles?: string[];
}

export interface UserManagementFilters {
  id?: string;
  userName?: string;
  systemRoles?: string;
  groups?: string;
  email?: string;
  role?: string;
  organizationName?: string;
  organizationType?: string;   // enum dropdown
  userState?: string;          // enum dropdown
  approvalDateFrom?: string;
  approvalDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  country?: string;
  adminManager?: string;
  referenceLicense?: string;
  agreementType?: string;
  agreementDuration?: string;
  paymentStatus?: string;
  lastLoginFrom?: string;      // yyyy-MM-dd
  lastLoginTo?: string;        // yyyy-MM-dd
  storage?: string;
}

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['../../app.component.scss', 'user-management.component.scss'],
  standalone: false
})
export class UserManagementComponent implements OnInit {
  modalTitle = 'User Management';
  users: UserManagementRow[] = [];
  allUsers: UserManagementRow[] = [];
  selected: boolean[] = [];

  filters: UserManagementFilters = {
    id: '',
    userName: '',
    email: '',
    role: '',
    organizationName: '',
    systemRoles: '',
    groups: '',
    organizationType: '',
    userState: '',
    approvalDateFrom: '',
    approvalDateTo: '',
    dueDateFrom: '',
    dueDateTo: '',
    country: '',
    adminManager: '',
    referenceLicense: '',
    agreementType: '',
    agreementDuration: '',
    paymentStatus: '',
    lastLoginFrom: '',
    lastLoginTo: '',
    storage: ''
  };

  pageIndex = 0;
  pageSize = 25;
  totalElements = 0;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = 'asc' ;

  organizationTypes: string[] = ['Customer', 'Partner', 'Internal'];
  userStates: string[] = ['Pending', 'Approved', 'Revoked', 'Admin'];
  agreementDurations: string[] = ['Unlimited', 'Limited'];
  agreementTypes: string[] = ['Free', 'Partial UPP', 'Full UPP'];

  loading = false;
  showFilters = false;


  expiringSoonDays = 30;
  inactivityDays = 365;

  constructor(
    public bsModalRef: BsModalRef,
    public bsModalService: BsModalService,
    private endpointService: EndpointsService,
    private http: HttpClient,
    private modalControlService: ModalControlService,
    private uiBlockService: UiBlockService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadPage();
  }


  loadPage(): void {
    this.loading = true;
    this.uiBlockService.startBlock('Fetching users...');

    this.http.get<UserManagementResponse>(
      this.endpointService.getUserManagement,
      this.endpointService.httpOptions
    ).pipe(
      finalize(() => {
        this.loading = false;
        this.uiBlockService.stopBlock();
      })
    ).subscribe({
      next: (response: UserManagementResponse) => {

        this.expiringSoonDays = response.expiringSoonDays ?? 30;
        this.inactivityDays = response.inactivityDays ?? 365;
        // Extract displayName from userApprovalRequestStr if present
        this.allUsers = (response.users || []).map(row => {
          if (row.userApprovalRequestStr) {
            try {
              const info = JSON.parse(row.userApprovalRequestStr);
              //GRIT ZONE change
              row.userName = info.firstName + ' ' + info.lastName;
            } catch {
              row.userName = row.userName;
            }
          }
          return row;
        });
        this.pageIndex = 0;
        this.applyFiltersAndPaging();
      },
      error: (e) => {
        console.error('Error fetching user management list.', e);
        this.modalControlService.showMesssageModal(
          this.modalTitle,
          'There was an error fetching users.'
        );
      }
    });
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  private applyFiltersAndPaging(): void {
      let filtered = [...this.allUsers];

      // ---- FILTERS ----
      if (this.filters.id) {
        const idNum = Number(this.filters.id);
        if (!isNaN(idNum)) {
          filtered = filtered.filter(u => u.id === idNum);
        }
      }

      if (this.filters.userName) {
        const v = this.filters.userName.toLowerCase();
        filtered = filtered.filter(u => (u.userName || '').toLowerCase().includes(v));
      }

      if (this.filters.systemRoles) {
        const v = this.filters.systemRoles.toLowerCase();
        filtered = filtered.filter(u => (u.systemRoles || []).some(r => r.toLowerCase().includes(v)));
      }
      if (this.filters.groups) {
        const v = this.filters.groups.toLowerCase();
        filtered = filtered.filter(u => (u.groups || []).some(g => g.toLowerCase().includes(v)));
      }

      if (this.filters.email) {
        const v = this.filters.email.toLowerCase();
        filtered = filtered.filter(u => (u.email || '').toLowerCase().includes(v));
      }

      if (this.filters.role) {
        const v = this.filters.role.toLowerCase();
        filtered = filtered.filter(u => (u.role || '').toLowerCase().includes(v));
      }

      if (this.filters.organizationName) {
        const v = this.filters.organizationName.toLowerCase();
        filtered = filtered.filter(u => (u.organizationName || '').toLowerCase().includes(v));
      }

      if (this.filters.organizationType) {
        const v = this.filters.organizationType.toLowerCase();
        filtered = filtered.filter(u => (u.organizationType || '').toLowerCase().includes(v));
      }

      if (this.filters.userState) {
        filtered = filtered.filter(u => u.userState === this.filters.userState);
      }

      // Approval Date Range Filter
      if (this.filters.approvalDateFrom) {
        const from = new Date(this.filters.approvalDateFrom);
        filtered = filtered.filter(u => {
          if (!u.approvalDate) return false;
          const d = new Date(u.approvalDate);
          return d >= from;
        });
      }
      if (this.filters.approvalDateTo) {
        const to = new Date(this.filters.approvalDateTo);
        filtered = filtered.filter(u => {
          if (!u.approvalDate) return false;
          const d = new Date(u.approvalDate);
          return d <= new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
        });
      }





      if (this.filters.dueDateFrom) {
        const from = new Date(this.filters.dueDateFrom);
        filtered = filtered.filter(u => {
          if (!u.dueDate) return false;
          const d = new Date(u.dueDate);
          return d >= from;
        });
      }
      if (this.filters.dueDateTo) {
        const to = new Date(this.filters.dueDateTo);
        filtered = filtered.filter(u => {
          if (!u.dueDate) return false;
          const d = new Date(u.dueDate);
          return d <= new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
        });
      }

      if (this.filters.country) {
        const v = this.filters.country.toLowerCase();
        filtered = filtered.filter(u => (u.country || '').toLowerCase().includes(v));
      }

      if (this.filters.adminManager) {
        const v = this.filters.adminManager.toLowerCase();
        filtered = filtered.filter(u => (u.adminManager || '').toLowerCase().includes(v));
      }

      if (this.filters.referenceLicense) {
        const v = this.filters.referenceLicense.toLowerCase();
        filtered = filtered.filter(u => (u.referenceLicense || '').toLowerCase().includes(v));
      }
      if (this.filters.agreementType) {
        filtered = filtered.filter(u => u.agreementType === this.filters.agreementType);
      }
      if (this.filters.agreementDuration) {
        filtered = filtered.filter(u => u.agreementDuration === this.filters.agreementDuration);
      }

      if (this.filters.paymentStatus) {
        const v = this.filters.paymentStatus.toLowerCase();
        filtered = filtered.filter(u => (u.paymentStatus || '').toLowerCase().includes(v));
      }

      if (this.filters.lastLoginFrom) {
        const from = new Date(this.filters.lastLoginFrom);
        filtered = filtered.filter(u => {
          if (!u.userLastLogin) return false;
          const d = new Date(u.userLastLogin);
          return d >= from;
        });
      }

      if (this.filters.lastLoginTo) {
        const to = new Date(this.filters.lastLoginTo);
        filtered = filtered.filter(u => {
          if (!u.userLastLogin) return false;
          const d = new Date(u.userLastLogin);
          return d <= new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
        });
      }

      if (this.filters.storage) {
        const v = this.filters.storage.toLowerCase();
        filtered = filtered.filter(u => (u.storage || '').toLowerCase().includes(v));
      }

      // ---- SORTING ----
      if (this.sortDirection !== '') {
      filtered = filtered.sort((a, b) => {
        const dir = this.sortDirection === 'asc' ? 1 : -1;
        let va: any;
        let vb: any;

        switch (this.sortColumn) {
            case 'agreementType':
              va = (a.agreementType || '').toLowerCase();
              vb = (b.agreementType || '').toLowerCase();
              break;
            case 'agreementDuration':
              va = (a.agreementDuration || '').toLowerCase();
              vb = (b.agreementDuration || '').toLowerCase();
              break;
            case 'paymentStatus':
              va = (a.paymentStatus || '').toLowerCase();
              vb = (b.paymentStatus || '').toLowerCase();
              break;
          case 'id':
            va = a.id;
            vb = b.id;
            break;
          case 'userName':
            va = (a.userName || '').toLowerCase();
            vb = (b.userName || '').toLowerCase();
            break;
          case 'email':
            va = (a.email || '').toLowerCase();
            vb = (b.email || '').toLowerCase();
            break;
          case 'role':
            va = (a.role || '').toLowerCase();
            vb = (b.role || '').toLowerCase();
            break;
          case 'organizationName':
            va = (a.organizationName || '').toLowerCase();
            vb = (b.organizationName || '').toLowerCase();
            break;
          case 'organizationType':
            va = (a.organizationType || '').toLowerCase();
            vb = (b.organizationType || '').toLowerCase();
            break;
          case 'userState':
            va = (a.userState || '').toLowerCase();
            vb = (b.userState || '').toLowerCase();
            break;
          case 'approvalDate':
            va = a.approvalDate ? new Date(a.approvalDate).getTime() : 0;
            vb = b.approvalDate ? new Date(b.approvalDate).getTime() : 0;
            break;
          case 'endDate':
          case 'dueDate':
            va = a.dueDate ? new Date(a.dueDate).getTime() : 0;
            vb = b.dueDate ? new Date(b.dueDate).getTime() : 0;
            break;
          case 'country':
            va = (a.country || '').toLowerCase();
            vb = (b.country || '').toLowerCase();
            break;
          case 'adminManager':
            va = (a.adminManager || '').toLowerCase();
            vb = (b.adminManager || '').toLowerCase();
            break;
          case 'referenceLicense':
            va = (a.referenceLicense || '').toLowerCase();
            vb = (b.referenceLicense || '').toLowerCase();
            break;
          case 'userLastLogin':
            va = a.userLastLogin ? new Date(a.userLastLogin).getTime() : 0;
            vb = b.userLastLogin ? new Date(b.userLastLogin).getTime() : 0;
            break;
          case 'storage':
            va = (a.storage || '').toLowerCase();
            vb = (b.storage || '').toLowerCase();
            break;
          case 'systemRoles':
              va = (a.systemRoles || []).join(', ').toLowerCase();
              vb = (b.systemRoles || []).join(', ').toLowerCase();
              break;
          case 'groups':
              va = (a.groups || []).join(', ').toLowerCase();
              vb = (b.groups || []).join(', ').toLowerCase();
              break;

          default:
            break;
        }

        if (va < vb) { return -1 * dir; }
        if (va > vb) { return 1 * dir; }
        return 0;
      });
    }
      // ---- PAGING ----
      this.totalElements = filtered.length;
      const start = this.pageIndex * this.pageSize;
      const end = start + this.pageSize;
      this.users = filtered.slice(start, end);

      this.selected = Array(this.users.length).fill(false);

  }



  onFiltersChanged(): void {
    this.pageIndex = 0;
    this.applyFiltersAndPaging();
  }

  updateFilter<K extends keyof UserManagementFilters>(key: K, value: string): void {
    this.filters[key] = value;
    this.onFiltersChanged();
  }

  updateLastLoginFrom(value: string): void {
    this.filters.lastLoginFrom = value;
    this.onFiltersChanged();
  }

  updateLastLoginTo(value: string): void {
    this.filters.lastLoginTo = value;
    this.onFiltersChanged();
  }

  updateDueDateFrom(value: string): void {
    this.filters.dueDateFrom = value;
    this.onFiltersChanged();
  }
  updateDueDateTo(value: string): void {
    this.filters.dueDateTo = value;
    this.onFiltersChanged();
  }

  updateApprovalDateFrom(value: string): void {
    this.filters.approvalDateFrom = value;
    this.onFiltersChanged();
  }

  updateApprovalDateTo(value: string): void {
    this.filters.approvalDateTo = value;
    this.onFiltersChanged();
  }
  changeSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFiltersAndPaging();
  }



  changePage(pageIndex: number, pageSize?: number): void {
    this.pageIndex = pageIndex;
    if (pageSize !== undefined) {
      this.pageSize = pageSize;
    }
    this.applyFiltersAndPaging();
  }

onPageSizeChange(newSize: number) {
  this.pageSize = +newSize;
  this.changePage(0, this.pageSize);
}


  resetFilters(): void {
    this.filters = {
      id: '',
      userName: '',
      systemRoles: '',
      groups: '',
      email: '',
      role: '',
      organizationName: '',
      organizationType: '',
      userState: '',
      approvalDateFrom: '',
      approvalDateTo: '',
      dueDateFrom: '',
      dueDateTo: '',
      country: '',
      adminManager: '',
      referenceLicense: '',
      agreementType: '',
      agreementDuration: '',
      paymentStatus: '',
      lastLoginFrom: '',
      lastLoginTo: '',
      storage: ''
    };

    this.sortDirection = '';
    this.pageIndex = 0;
    this.applyFiltersAndPaging();
  }


  selectAll(): void {
    this.selected.fill(true);
  }

  deselectAll(): void {
    this.selected.fill(false);
  }

  toggleRowSelected(index: number): void {
    this.selected[index] = !this.selected[index];
    this.cdr.detectChanges();
  }

  atLeastOneRowSelected(): boolean {
    return this.selected.some(x => x);
  }

  getSelectedRows(): UserManagementRow[] {
    const selectedRows: UserManagementRow[] = [];
    for (let i = 0; i < this.users.length; i++) {
      if (this.selected[i]) {
        selectedRows.push(this.users[i]);
      }
    }
    return selectedRows;
  }

  getSelectedUsers(): UserManagementRow[] {
    return this.users.filter((u, i) => this.selected[i]);
  }

  get totalPages(): number {
    if (!this.totalElements || this.pageSize === 0) return 1;
    return Math.ceil(this.totalElements / this.pageSize);
  }


  openUserApprovalSingle(row: UserManagementRow): void {
    const initialState = {
      users: [row]
    };
    const bsModalRef: BsModalRef = this.bsModalService.show(UserApprovalComponent, { initialState });

    (bsModalRef.content as UserApprovalComponent).confirm.subscribe((form: UserApprovalForm) => {
      this.applyUserManagementAction(form, false);
    });

  }

  openUserApprovalBulk(): void {
    const selectedRows = this.getSelectedRows();

    if (!selectedRows || selectedRows.length === 0) {
      this.modalControlService.showMesssageModal(
        this.modalTitle,
        'Please select at least one user for bulk actions.'
      );
      return;
    }

    // Bulk validation: all must have same state, orgName, orgType, country
    const stateSet = new Set(selectedRows.map(u => u.userState));
    const orgNameSet = new Set(selectedRows.map(u => u.organizationName));
    const orgTypeSet = new Set(selectedRows.map(u => u.organizationType));
    const countrySet = new Set(selectedRows.map(u => u.country));
    const referenceLicenseSet = new Set(selectedRows.map(u => u.referenceLicense));
    const dueDateSet = new Set(selectedRows.map(u => u.dueDate));


    if (stateSet.size > 1) {
      this.modalControlService.showMesssageModal(
        this.modalTitle,
        'The selected users have different states. Please select users with the same state for bulk actions.'
      );
      return;
    }
    if (orgNameSet.size > 1) {
      this.modalControlService.showMesssageModal(
        this.modalTitle,
        'The selected users have different Organization Names. Please select users with the same Organization Name for bulk actions.'
      );
      return;
    }
    if (orgTypeSet.size > 1) {
      this.modalControlService.showMesssageModal(
        this.modalTitle,
        'The selected users have different Organization Types. Please select users with the same Organization Type for bulk actions.'
      );
      return;
    }
    if (countrySet.size > 1) {
      this.modalControlService.showMesssageModal(
        this.modalTitle,
        'The selected users have different Countries. Please select users with the same Country for bulk actions.'
      );
      return;
    }
    if (referenceLicenseSet.size > 1) {
      this.modalControlService.showMesssageModal(
        this.modalTitle,
        'The selected users have different Reference Licenses. Please select users with the same Reference License for bulk actions.'
      );
      return;
    }
    if (dueDateSet.size > 1) {
      this.modalControlService.showMesssageModal(
        this.modalTitle,
        'The selected users have different Due Dates. Please select users with the same Due Date for bulk actions.'
      );
      return;
    }

    const initialState = {
      users: selectedRows
    };




    const modalRef: BsModalRef = this.bsModalService.show(UserApprovalComponent, { initialState });
    (modalRef.content as UserApprovalComponent).confirm.subscribe((form: UserApprovalForm) => {
     /* const msgLines = [
        `Bulk update for user IDs: ${form.targetUsers.map(u => u.id).join(', ')}`,
        `New state: ${form.newState}`,
        `License type: ${form.licenseType}`,
        `Due date: ${form.dueDate || '-'}`
      ];
      this.modalControlService.showMultipleMesssagesModal(
        this.modalTitle,
        msgLines
      );*/
      this.applyUserManagementAction(form, true);
    });
  }




  exportUsers(): void {
    const selectedUsers = this.getSelectedUsers();

    if (!selectedUsers || selectedUsers.length === 0) {
      return;
    }

    const header = [
      'UserName',
      'Email',
      'SystemRoles',
      'Groups',
      'Role',
      'OrganisationName',
      'OrganisationType',
      'Status',
      'ApprovalDate',
      'ExpiryDate',
      'Country',
      'AdminManager',
      'ReferenceLicense',
      'AgreementType',
      'AgreementDuration',
      'PaymentStatus',
      'UserLastLogin'
      //,'Storage'
    ];

    const lines: string[] = [];

    lines.push(header.join(';'));

    for (const u of selectedUsers) {
      const row = [
        this.csvSafe(u.userName),
        this.csvSafe(u.email),
        this.csvSafe((u.systemRoles || []).join('|')),
        this.csvSafe((u.groups || []).join('|')),
        this.csvSafe(u.role),
        this.csvSafe(u.organizationName),
        this.csvSafe(u.organizationType),
        this.csvSafe(u.userState),
        this.csvSafe(u.approvalDate),
        this.csvSafe(u.dueDate),
        this.csvSafe(u.country),
        this.csvSafe(u.adminManager),
        this.csvSafe(u.referenceLicense),
        this.csvSafe(u.agreementType),
        this.csvSafe(u.agreementDuration),
        this.csvSafe(u.paymentStatus),
        this.csvSafe(
          u.userLastLogin
            ? (new Date(u.userLastLogin)).toISOString()
            : ''
        )
        //,this.csvSafe(u.storage)
      ];

      lines.push(row.join(';'));
    }

    const csvContent = '\uFEFF' + lines.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'users.csv';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }
  private csvSafe(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    let str = String(value);

    str = str.replace(/\r\n/g, ' ').replace(/\n/g, ' ');

    str = str.replace(/;/g, ' ');

    return str;
  }

  private applyUserManagementAction(form: UserApprovalForm, isBulk: boolean): void {
    const emails = (form.targetUsers || []).map(u => u.email);

    if (!emails || emails.length === 0) {
      this.modalControlService.showMesssageModal(
        'User Management',
        'No users selected for update.'
      );
      return;
    }

    const payload = {
      emails,
      newState: form.newState,
      agreementDuration: form.agreementDuration,
      agreementType: form.agreementType,
      paymentStatus: form.paymentStatus,
      dueDate: form.dueDate ,

      referenceLicense: form.referenceLicense,
      role: isBulk ? null: form.role ,
      country: form.country,
      organizationName: form.organizationName,
      organizationType: form.organizationType,

      groups: isBulk ? null : form.groups,
      systemRoles: isBulk ? null : form.systemRoles



    };

    this.uiBlockService.startBlock('Applying update...');

    this.http.post(this.endpointService.applyAction, payload, this.endpointService.httpOptions)
      .pipe(finalize(() => this.uiBlockService.stopBlock()))
      .subscribe({
        next: () => {
          this.modalControlService.showMesssageModal(
            'User Management',
            'User(s) updated successfully.'
          );
          this.loadPage();
        },
        error: (err) => {
          console.error('User management action failed', err);
          this.modalControlService.showMesssageModal(
            'User Management',
            'An error occurred while applying the action.'
          );
        }
      });
  }

  exportLicenseManagementHistory(): void {
    this.uiBlockService.startBlock('Exporting license management history...');
    this.http.get(
      this.endpointService.exportLicenseManagementHistory,
      { ...this.endpointService.fileHttpOptions, observe: 'response', responseType: 'blob' }
    )
    .pipe(finalize(() => this.uiBlockService.stopBlock()))
    .subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.modalControlService.showMesssageModal(
            'Export Error',
            'Received an empty export file.'
          );
          return;
        }
        let fileName = 'user-management-history.csv';
        const contentDisposition = response.headers.get('content-disposition');

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) {
            fileName = match[1];
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.modalControlService.showMesssageModal(
          'Export Error',
          'Failed to export user management history.'
        );
      }
    });
  }



getRowClasses(user: UserManagementRow, index: number): { [klass: string]: boolean } {
  const isSelected = this.selected[index];
  const inactive = this.isInactive(user);
  const expiring = this.isLicenseExpiringSoon(user);

  return {
    'selected-row': isSelected,
    'row-expiring': !isSelected && expiring,
    'row-inactive': !isSelected && !expiring && inactive
  };
}



isLicenseExpiringSoon(user: UserManagementRow): boolean {
  if (user.userState !== 'Approved') return false;
  if (!user.dueDate) return false;

  const dueDate = new Date(user.dueDate);
  if (isNaN(dueDate.getTime())) return false;

  const today = this.startOfDay(new Date());
  const due = this.startOfDay(dueDate);

  const diffMs = due.getTime() - today.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return false;
  return diffDays <= this.expiringSoonDays;
}


isInactive(user: UserManagementRow): boolean {
  // Only approved users can be inactive
  if (user.userState !== 'Approved') {
    return false;
  }

  let referenceDate: Date | null = null;

  if (user.userLastLogin) {
    const lastLogin = new Date(user.userLastLogin);
    if (!isNaN(lastLogin.getTime())) {
      referenceDate = lastLogin;
    }
  }
  // fallback to approvalDate if no login
  if (!referenceDate && user.approvalDate) {
    const approvalDate = new Date(user.approvalDate);
    if (!isNaN(approvalDate.getTime())) {
      referenceDate = approvalDate;
    }
  }

  if (!referenceDate) return false;

  const today = this.startOfDay(new Date());
  const refDay = this.startOfDay(referenceDate);

  const diffMs = today.getTime() - refDay.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return diffDays >= this.inactivityDays;
}

private startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}





}
